import { useQuery } from "@tanstack/react-query";
import ViewLayout from "@/v2/layout/View";
import { CardContent } from "@/components/ui/card";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { Separator } from "@/components/ui/separator";

import {
    Building2,
    MapPin,
    Phone,
    Mail,
    Globe,
    Landmark,
    User,
    Hash,
    Banknote,
    Pencil,
} from "lucide-react";
import FindArProfile from "@/v2/api/ar-profile/Find";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const NavButtons = ({ id }: { id: string }) => {
    return (
        <>
            <div className="flex items-center gap-2">
                <Link to={`/account/company-details/edit/${id}`}>
                    <Button variant={"outline"} size={"sm"}>
                        <Pencil className="size-4" /> Edit
                    </Button>
                </Link>
            </div>
        </>
    );
};

export default function ArProfilePage() {
    const query = useQuery({
        queryKey: ["ar-profile"],
        queryFn: () => FindArProfile(),
    });

    if (query.isLoading) return <LoadingAnimation />;

    if (!query.data) return <p>Failed to load AR Profile...</p>;

    const profile = query.data;

    const rep = profile.representative_person;

    return (
        <ViewLayout
            title="AR Profile"
            description="Accounts Receivable Profile Information"
            icon={Building2}
            buttons={<NavButtons id={query?.data?.id} />}
        >
            <CardContent className="space-y-10">
                {/* Company Info */}
                <section>
                    <h3 className="mb-4 text-lg font-semibold">
                        Company Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <InfoBlock
                            icon={Hash}
                            label="Company Code"
                            value={profile.company_code}
                        />
                        <InfoBlock
                            icon={Building2}
                            label="Company Name"
                            value={profile.company_name}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Address"
                            value={profile.address}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="City"
                            value={profile.city}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="State"
                            value={profile.state}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Area"
                            value={profile.area}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Country"
                            value={profile.country}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Pincode"
                            value={profile.pincode}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Building"
                            value={profile.building}
                        />
                    </div>
                </section>

                <Separator />

                {/* Contact Info */}
                <section>
                    <h3 className="mb-4 text-lg font-semibold">
                        Contact Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <InfoBlock
                            icon={Phone}
                            label="Phone Number"
                            value={profile.phone_number}
                        />
                        <InfoBlock
                            icon={Mail}
                            label="Email"
                            value={profile.email}
                        />
                        <InfoBlock
                            icon={Globe}
                            label="Website"
                            value={profile.website}
                        />

                        <InfoBlock
                            icon={Hash}
                            label="Tax Reg. No"
                            value={profile.tax_reg_no}
                        />
                    </div>
                </section>

                <Separator />

                {/* Representative */}
                {rep && (
                    <section>
                        <h3 className="mb-4 text-lg font-semibold">
                            Representative
                        </h3>
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <InfoBlock
                                icon={User}
                                label="Name"
                                value={rep.full_name}
                            />
                            <InfoBlock
                                icon={Phone}
                                label="Phone"
                                value={rep.phone_number}
                            />
                            <InfoBlock
                                icon={Mail}
                                label="Email"
                                value={rep.email}
                            />
                        </div>
                    </section>
                )}

                <Separator />

                {/* Banking Info */}
                <section>
                    <h3 className="mb-4 text-lg font-semibold">
                        Banking Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <InfoBlock
                            icon={Landmark}
                            label="Bank Name"
                            value={profile.bank_name}
                        />
                        <InfoBlock
                            icon={MapPin}
                            label="Bank Address"
                            value={profile.bank_address}
                        />
                        <InfoBlock
                            icon={Banknote}
                            label="IBAN"
                            value={profile.iban}
                        />
                        <InfoBlock
                            icon={Hash}
                            label="Account Number"
                            value={profile.account_number}
                        />
                    </div>
                </section>
            </CardContent>
        </ViewLayout>
    );
}

/* Reusable info block component */
function InfoBlock({
    icon: Icon,
    label,
    value,
}: {
    icon: any;
    label: string;
    value: string | undefined;
}) {
    return (
        <div className="space-y-2">
            <div className="flex items-center text-sm text-muted-foreground">
                <Icon className="mr-2 h-4 w-4" />
                {label}
            </div>
            <p className="break-all text-base font-medium">{value || "N/A"}</p>
        </div>
    );
}
